// ui.tsx — ReNurture material primitives. Layered, tactile, warm.
// Ported from the design system (guide/.../refined/ui.jsx) into typed React.
import React from 'react';
import type { DisplayTitle } from '@/types';
import { Icon } from './icons';

type CSS = React.CSSProperties;

// ─────────────────────────── TYPE ───────────────────────────
export function Display({
  parts,
  text,
  size = 24,
  lh = 1.14,
  weight = 400,
  color,
  emColor,
  style = {},
  as: As = 'div',
}: {
  parts?: DisplayTitle;
  text?: string;
  size?: number;
  lh?: number;
  weight?: number;
  color?: string;
  emColor?: string;
  style?: CSS;
  as?: React.ElementType;
}) {
  const root: CSS = {
    fontFamily: 'Fraunces, Georgia, serif',
    fontWeight: weight,
    fontSize: size,
    lineHeight: lh,
    letterSpacing: '-0.018em',
    color: color || 'var(--ink)',
    textWrap: 'pretty' as CSS['textWrap'],
    margin: 0,
    ...style,
  };
  if (text != null) return <As style={root}>{text}</As>;
  if (!parts) return null;
  return (
    <As style={root}>
      {parts.pre}
      <span style={{ fontStyle: 'italic', fontWeight: weight, color: emColor || 'var(--amber-deep)' }}>
        {parts.em}
      </span>
      {parts.post}
    </As>
  );
}

export function Eyebrow({
  children,
  color,
  style = {},
}: {
  children: React.ReactNode;
  color?: string;
  style?: CSS;
}) {
  return (
    <div
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: 10.5,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: color || 'var(--amber-deep)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Body({
  children,
  dim,
  size = 15,
  style = {},
  role,
}: {
  children: React.ReactNode;
  dim?: boolean;
  size?: number;
  style?: CSS;
  role?: string;
}) {
  return (
    <p
      role={role}
      style={{
        margin: 0,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: size,
        lineHeight: 1.62,
        color: dim ? 'var(--ink-soft)' : 'var(--ink)',
        letterSpacing: '-0.003em',
        textWrap: 'pretty' as CSS['textWrap'],
        ...style,
      }}
    >
      {children}
    </p>
  );
}

export function Meta({ children, style = {} }: { children: React.ReactNode; style?: CSS }) {
  return (
    <span
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 11.5,
        color: 'var(--ink-soft)',
        letterSpacing: '0.005em',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ─────────────────────────── TACTILE ───────────────────────────
export function Pressable({
  children,
  onClick,
  style = {},
  as: As = 'div',
  className = '',
  ariaLabel,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  style?: CSS;
  as?: React.ElementType;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const press = (ev: React.PointerEvent) => {
    (ev.currentTarget as HTMLElement).style.transform = 'scale(0.982)';
    (ev.currentTarget as HTMLElement).style.filter = 'brightness(0.975)';
  };
  const release = (ev: React.PointerEvent) => {
    (ev.currentTarget as HTMLElement).style.transform = '';
    (ev.currentTarget as HTMLElement).style.filter = '';
  };
  return (
    <As
      onClick={disabled ? undefined : onClick}
      className={className}
      aria-label={ariaLabel}
      disabled={As === 'button' ? disabled : undefined}
      style={{
        transition: 'transform .2s cubic-bezier(.2,.7,.3,1), filter .2s ease',
        cursor: onClick && !disabled ? 'pointer' : 'default',
        ...style,
      }}
      onPointerDown={onClick && !disabled ? press : undefined}
      onPointerUp={onClick && !disabled ? release : undefined}
      onPointerLeave={onClick && !disabled ? release : undefined}
    >
      {children}
    </As>
  );
}

// ─────────────────────────── SURFACES ───────────────────────────
export function Card({
  children,
  style = {},
  onClick,
  pad = 17,
  level = 1,
  glow,
  well,
  radius,
  className = '',
}: {
  children: React.ReactNode;
  style?: CSS;
  onClick?: (e: React.MouseEvent) => void;
  pad?: number | string;
  level?: 1 | 2;
  glow?: boolean;
  well?: boolean;
  radius?: number;
  className?: string;
}) {
  const r = radius != null ? radius : 'var(--r-card)';
  let base: CSS;
  if (well) {
    base = {
      background: 'var(--paper)',
      boxShadow: 'inset 0 2px 6px rgba(74,52,24,0.08), inset 0 0 0 1px rgba(74,52,24,0.045)',
      border: 'none',
    };
  } else if (level === 2) {
    base = {
      background: 'linear-gradient(176deg, var(--card2-top), var(--card2-bot))',
      border: '1px solid rgba(200,134,26,0.18)',
      boxShadow: 'var(--edge), var(--shf)',
    };
  } else {
    base = {
      background: 'linear-gradient(178deg, var(--card-top), var(--card-bot))',
      border: '1px solid var(--hair)',
      boxShadow: 'var(--edge), var(--sh1)',
    };
  }
  const inner = (
    <>
      {glow && (
        <div
          style={{
            position: 'absolute',
            top: -78,
            right: -46,
            width: 230,
            height: 230,
            borderRadius: '50%',
            background: 'radial-gradient(circle, var(--glowwarm), transparent 66%)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div style={{ position: 'relative' }}>{children}</div>
    </>
  );
  const st: CSS = {
    position: 'relative',
    borderRadius: r,
    padding: pad,
    boxSizing: 'border-box',
    overflow: 'hidden',
    ...base,
    ...style,
  };
  if (onClick)
    return (
      <Pressable className={className} onClick={onClick} style={st}>
        {inner}
      </Pressable>
    );
  return (
    <div className={className} style={st}>
      {inner}
    </div>
  );
}

export function Glass({
  children,
  style = {},
  pad = 14,
  radius = 22,
  dark,
}: {
  children: React.ReactNode;
  style?: CSS;
  pad?: number;
  radius?: number;
  dark?: boolean;
}) {
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: radius,
        padding: pad,
        boxSizing: 'border-box',
        background: dark ? 'rgba(28,20,12,0.55)' : 'var(--frost)',
        backdropFilter: 'blur(22px) saturate(150%)',
        WebkitBackdropFilter: 'blur(22px) saturate(150%)',
        border: dark ? '1px solid rgba(246,224,190,0.12)' : '1px solid rgba(255,255,255,0.7)',
        boxShadow: dark
          ? '0 1px 0 rgba(255,236,205,0.08) inset, 0 18px 44px -20px rgba(0,0,0,0.65)'
          : 'inset 0 1px 0 rgba(255,255,255,0.85), 0 16px 36px -18px rgba(74,52,24,0.32)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Cinematic tonal placeholder (evening light in a warm-dark room). Optionally
// shows a real poster image on top, unified into the palette by the duotone.
export function Plate({
  ratio = '16 / 9',
  radius = 0,
  caption,
  glow = '38% 30%',
  poster,
  overlay,
  style = {},
}: {
  ratio?: string;
  radius?: number;
  caption?: string;
  glow?: string;
  poster?: string;
  overlay?: React.ReactNode;
  style?: CSS;
}) {
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: radius,
        overflow: 'hidden',
        aspectRatio: ratio,
        background: 'radial-gradient(120% 130% at 28% 14%, #2a1d10 0%, #1d140b 52%, #120c06 100%)',
        ...style,
      }}
    >
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(58% 76% at ${glow}, rgba(232,176,84,0.34), rgba(190,128,46,0.10) 46%, transparent 70%)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.5,
          mixBlendMode: 'soft-light',
          backgroundImage:
            'repeating-linear-gradient(180deg, rgba(255,240,214,0.05) 0px, rgba(255,240,214,0.05) 1px, transparent 1px, transparent 4px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          boxShadow:
            'inset 0 1px 0 rgba(255,240,214,0.1), inset 0 0 120px 24px rgba(10,6,2,0.5), inset 0 -50px 60px -30px rgba(10,6,2,0.7)',
        }}
      />
      {caption && (
        <div
          style={{
            position: 'absolute',
            left: 13,
            bottom: 11,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 9.5,
            letterSpacing: '0.06em',
            color: 'rgba(246,224,190,0.46)',
            textTransform: 'lowercase',
          }}
        >
          {caption}
        </div>
      )}
      {overlay}
    </div>
  );
}

// ─────────────────────────── CONTROLS ───────────────────────────
type BtnVariant = 'primary' | 'amber' | 'soft' | 'ghost';
export function Btn({
  children,
  variant = 'primary',
  size = 'md',
  full,
  onClick,
  style = {},
  leading,
  disabled,
  ariaLabel,
}: {
  children: React.ReactNode;
  variant?: BtnVariant;
  size?: 'sm' | 'md' | 'lg';
  full?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  style?: CSS;
  leading?: React.ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const pads = size === 'lg' ? '15px 26px' : size === 'sm' ? '9px 16px' : '12px 22px';
  const fs = size === 'lg' ? 15.5 : size === 'sm' ? 13 : 14.5;
  const base: CSS = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 600,
    fontSize: fs,
    padding: pads,
    borderRadius: 999,
    border: '1px solid transparent',
    width: full ? '100%' : undefined,
    minHeight: 44,
    letterSpacing: '-0.005em',
    userSelect: 'none',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
  const variants: Record<BtnVariant, CSS> = {
    primary: {
      background: 'linear-gradient(180deg, #27B98C, var(--green))',
      color: '#fff',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 2px rgba(20,80,55,0.25), 0 12px 26px -10px rgba(22,122,90,0.5)',
    },
    amber: {
      background: 'linear-gradient(180deg, #E1A943, var(--amber))',
      color: '#fff',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.42), 0 12px 26px -10px rgba(155,103,18,0.5)',
    },
    soft: {
      background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
      color: 'var(--ink)',
      border: '1px solid var(--hair-strong)',
      boxShadow: 'var(--edge), var(--sh1)',
    },
    ghost: { background: 'transparent', color: 'var(--ink)', border: '1px solid var(--hair-strong)' },
  };
  return (
    <Pressable
      as="button"
      onClick={onClick}
      disabled={disabled}
      ariaLabel={ariaLabel}
      style={{ ...base, ...variants[variant], ...style }}
    >
      {leading}
      {children}
    </Pressable>
  );
}

export function Chip({
  children,
  active,
  onClick,
  style = {},
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  style?: CSS;
}) {
  return (
    <Pressable
      as="button"
      onClick={onClick}
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 500,
        fontSize: 13.5,
        padding: '8px 15px',
        minHeight: 40,
        borderRadius: 999,
        background: active
          ? 'linear-gradient(180deg,#E1A943,var(--amber))'
          : 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
        color: active ? '#fff' : 'var(--ink)',
        border: active ? '1px solid var(--amber)' : '1px solid var(--hair)',
        letterSpacing: '-0.005em',
        boxSizing: 'border-box',
        boxShadow: active
          ? 'inset 0 1px 0 rgba(255,255,255,0.4), 0 7px 16px -8px rgba(200,134,26,0.42)'
          : 'var(--edge), var(--sh1)',
        ...style,
      }}
    >
      {children}
    </Pressable>
  );
}

export function Ring({
  pct,
  size = 44,
  sw = 4,
  color = 'var(--amber)',
  track = 'rgba(74,52,24,0.14)',
  children,
}: {
  pct: number;
  size?: number;
  sw?: number;
  color?: string;
  track?: string;
  children?: React.ReactNode;
}) {
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={sw} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      {children && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function Beads({
  total,
  done,
  current,
  color = 'var(--green)',
}: {
  total: number;
  done: number;
  current?: boolean;
  color?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {Array.from({ length: total }).map((_, i) => {
        const isDone = i < done;
        const isCur = i === done && current;
        return (
          <span
            key={i}
            style={{
              width: isCur ? 16 : 6,
              height: 6,
              borderRadius: 999,
              background: isDone || isCur ? color : 'var(--hair-strong)',
              opacity: isDone || isCur ? 1 : 0.55,
              transition: 'all .3s ease',
            }}
          />
        );
      })}
    </div>
  );
}

export function StageRail({
  count,
  currentIndex,
  doneBefore,
}: {
  count: number;
  currentIndex: number;
  doneBefore: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {Array.from({ length: count }).map((_, i) => {
        const done = i < doneBefore;
        const cur = i === currentIndex;
        return (
          <span
            key={i}
            style={{
              height: 4,
              borderRadius: 999,
              flex: cur ? 2.4 : 1,
              background: done ? 'var(--green)' : cur ? 'var(--amber)' : 'var(--hair-strong)',
              transition: 'all .3s ease',
            }}
          />
        );
      })}
    </div>
  );
}

export type PillState = 'done' | 'current' | 'open' | 'soon';
export function StatePill({ state, onLight }: { state: PillState; onLight?: boolean }) {
  const map: Record<PillState, { t: string; c: string; bg: string }> = {
    done: { t: 'Completed', c: 'var(--green-deep)', bg: 'rgba(29,158,117,0.13)' },
    current: { t: 'In progress', c: 'var(--amber-deep)', bg: 'rgba(200,134,26,0.15)' },
    open: { t: 'Open', c: 'var(--ink-soft)', bg: 'var(--paper)' },
    soon: { t: 'Opens as you go', c: 'var(--ink-soft)', bg: 'transparent' },
  };
  const mm = map[state];
  const light = onLight
    ? { color: '#fff', background: 'rgba(255,255,255,0.2)' }
    : { color: mm.c, background: mm.bg };
  return (
    <span
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.02em',
        padding: '4px 10px',
        borderRadius: 999,
        border: state === 'soon' && !onLight ? '1px solid var(--hair-strong)' : 'none',
        whiteSpace: 'nowrap',
        backdropFilter: onLight ? 'blur(6px)' : 'none',
        ...light,
      }}
    >
      {mm.t}
    </span>
  );
}

export function IconButton({
  name,
  size = 19,
  onClick,
  style = {},
  color,
  sw,
  label,
}: {
  name: React.ComponentProps<typeof Icon>['name'];
  size?: number;
  onClick?: () => void;
  style?: CSS;
  color?: string;
  sw?: number;
  label: string;
}) {
  return (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel={label}
      style={{
        width: 44,
        height: 44,
        borderRadius: 999,
        border: '1px solid var(--hair)',
        background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color || 'var(--ink)',
        flexShrink: 0,
        boxShadow: 'var(--edge), var(--sh1)',
        ...style,
      }}
    >
      <Icon name={name} size={size} sw={sw} />
    </Pressable>
  );
}

export function GlyphWell({
  children,
  size = 46,
  color = 'var(--amber-deep)',
  radius = 13,
  tone = 'amber',
}: {
  children: React.ReactNode;
  size?: number;
  color?: string;
  radius?: number;
  tone?: 'amber' | 'green';
}) {
  const bg = tone === 'green' ? 'rgba(29,158,117,0.1)' : 'rgba(200,134,26,0.1)';
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        background: bg,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 0 0 1px rgba(74,52,24,0.05)',
      }}
    >
      {children}
    </div>
  );
}

export function PlayDisc({
  size = 46,
  onClick,
  variant = 'green',
  onImage,
  label = 'Play',
}: {
  size?: number;
  onClick?: (e: React.MouseEvent) => void;
  variant?: 'green' | 'amber';
  onImage?: boolean;
  label?: string;
}) {
  const bg = onImage
    ? 'rgba(255,250,242,0.94)'
    : variant === 'amber'
      ? 'linear-gradient(180deg,#E1A943,var(--amber))'
      : 'linear-gradient(180deg,#27B98C,var(--green))';
  const col = onImage ? 'var(--ink)' : '#fff';
  return (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel={label}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        border: 'none',
        background: bg,
        color: col,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: onImage
          ? '0 8px 22px -7px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.6)'
          : 'inset 0 1px 0 rgba(255,255,255,0.4), 0 10px 22px -9px rgba(22,122,90,0.55)',
      }}
    >
      <Icon name="play" size={size * 0.4} style={{ marginLeft: 1 }} />
    </Pressable>
  );
}

// ─────────────────────────── helpers ───────────────────────────
export function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
export function mins(sec: number): string {
  return `${Math.round(sec / 60)} min`;
}
